import { Controller } from "@hotwired/stimulus"

// FD-483: chart.js (~0.4 MB source) only runs on analytics/burndown/error-group
// surfaces, so it stays out of application.js. Under esbuild --splitting this
// dynamic import becomes a separate chunk, fetched once (then HTTP-cached) the
// first time a chart actually mounts — list/board pages never download it.
let chartPromise

function loadChart() {
  chartPromise ||= import("chart.js").then((mod) => {
    const {
      Chart, LineController, LineElement, PointElement, BarController, BarElement,
      PieController, DoughnutController, ArcElement, CategoryScale, LinearScale,
      Tooltip, Legend, Filler
    } = mod
    Chart.register(LineController, LineElement, PointElement, BarController, BarElement,
      PieController, DoughnutController, ArcElement, CategoryScale, LinearScale, Tooltip, Legend, Filler)
    return Chart
  })
  return chartPromise
}

// Renders a chart from JSON config embedded in data attributes.
export default class extends Controller {
  static values = { type: String, data: Object, options: Object }

  async connect() {
    // Guard the await: if this element disconnects (or Turbo re-connects it)
    // while the chunk is still downloading, the stale connect must not build a
    // chart on a dead canvas.
    const token = (this.connectToken = {})
    const Chart = await loadChart()
    if (this.connectToken !== token || !this.element.isConnected) return

    const style = getComputedStyle(document.documentElement)
    Chart.defaults.color = style.getPropertyValue("--sys-color-on-surface-variant").trim() || "#999"
    Chart.defaults.borderColor = style.getPropertyValue("--sys-color-outline-variant-default").trim() || "#333"
    Chart.defaults.font.family = "Inter, sans-serif"
    Chart.defaults.font.size = 11

    this.chart = new Chart(this.element, {
      type: this.typeValue,
      data: this.dataValue,
      options: { responsive: true, maintainAspectRatio: false, ...this.optionsValue }
    })
  }

  disconnect() {
    this.connectToken = null
    this.chart?.destroy()
    this.chart = null
  }
}
